import OLMap from 'ol/Map';
import View from 'ol/View';
import { fromLonLat, toLonLat } from 'ol/proj';
import { defaults as defaultControls } from 'ol/control';
import { store } from './state';
import { observationsAPI, traversesAPI, vesselsAPI, expeditionsAPI, sitesAPI } from './api';
import { getLayerById, getEffectiveVisibility } from './layers'; // <-- Импортируем отсюда
import type { ObservationFull, TraverseInfo } from './types';

export let map: OLMap;

// ============================================================================
// Вспомогательные функции обогащения данных
// ============================================================================

function isValidUUID(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

async function enrichObservation(obs: ObservationFull): Promise<ObservationFull> {
  const enriched = { ...obs } as any;
  try {
    if (typeof enriched.traverse === 'string') {
      enriched.traverse = await traversesAPI.getFull(enriched.traverse);
    }
    if (enriched.traverse && typeof enriched.traverse === 'object') {
      const t = enriched.traverse as any;
      if (typeof t.vessel === 'string') {
        try { t.vessel = await vesselsAPI.get(t.vessel); } 
        catch (e) { console.warn('Не удалось загрузить судно', e); t.vessel = { name: 'Неизвестно' }; }
      }
      if (typeof t.expedition === 'string') {
        try { t.expedition = await expeditionsAPI.get(t.expedition); } 
        catch (e) { console.warn('Не удалось загрузить экспедицию', e); t.expedition = { name: 'Неизвестно' }; }
      }
    }
    if (typeof enriched.site === 'string') {
      try { enriched.site = await sitesAPI.get(enriched.site); } 
      catch (e) { console.warn('Не удалось загрузить станцию', e); enriched.site = null; }
    }
  } catch (error) {
    console.error('❌ Критическая ошибка при обогащении точки:', error);
  }
  return enriched as ObservationFull;
}

async function enrichTraverse(t: TraverseInfo): Promise<TraverseInfo> {
  const enriched = { ...t } as any;
  try {
    if (typeof enriched.expedition === 'string') {
      try { enriched.expedition = await expeditionsAPI.get(enriched.expedition); } 
      catch (e) { console.warn('Не удалось загрузить экспедицию', e); enriched.expedition = { name: 'Неизвестно' }; }
    }
    if (typeof enriched.vessel === 'string') {
      try { enriched.vessel = await vesselsAPI.get(enriched.vessel); } 
      catch (e) { console.warn('Не удалось загрузить судно', e); enriched.vessel = { name: 'Неизвестно' }; }
    }
  } catch (error) {
    console.error('❌ Ошибка обогащения маршрута:', error);
  }
  return enriched as TraverseInfo;
}

// ============================================================================
// Инициализация карты
// ============================================================================

export function initMap(): OLMap {
  map = new OLMap({
    target: 'map',
    layers: [], // <-- ПУСТО! Слои добавит модуль layers.ts
    controls: defaultControls({ zoom: true, attribution: true, rotate: false }),
    view: new View({
      center: fromLonLat([107.0, 53.0]), // Байкал по умолчанию
      zoom: 6,
      minZoom: 3,
      maxZoom: 18
    })
  });

  map.on('singleclick', handleMapClick);

  return map;
}

// ============================================================================
// Обработчик клика по карте
// ============================================================================

async function handleMapClick(evt: any) {
  const viewResolution = map.getView().getResolution();
  if (!viewResolution) return;

  // 1. Пытаемся найти точку наблюдения
  const observationId = await getFeatureIdFromLayer('observations', evt, viewResolution);
  if (observationId && isValidUUID(observationId)) {
    console.log('📥 Запрос данных для точки:', observationId);

    try {
      const rawObservation = await observationsAPI.get(observationId);
      const observation = await enrichObservation(rawObservation);
            
      const coords = getFeatureCoordinates('observations', evt);
      if (coords) {
        (observation as any).latitude = coords.lat;
        (observation as any).longitude = coords.lon;
      }
            
      store.dispatch({ type: 'SELECT_OBSERVATION', payload: observation });
  
      const { insituResultsAPI, labResultsAPI } = await import('./api');
      const [insituRes, labRes] = await Promise.all([
        insituResultsAPI.list({ observation: observationId }),
        labResultsAPI.list({ observation: observationId })
      ]);

      store.dispatch({ type: 'SET_INSITU_SAMPLES', payload: insituRes.data || [] });
      store.dispatch({ type: 'SET_LAB_SAMPLES', payload: labRes.data || [] });

      return;
    } catch {
      console.log('Ошибка получения точки');
      store.dispatch({ type: 'SET_INSITU_SAMPLES', payload: [] });
      store.dispatch({ type: 'SET_LAB_SAMPLES', payload: [] });
    }
  }
  // 2. Пытаемся найти станцию мониторинга (WMS или WFS)
  const siteId = await getFeatureIdFromLayer('sites', evt, viewResolution);
  console.log('siteId = ', siteId)
  if (siteId && isValidUUID(siteId)) {
    console.log('📍 Запрос данных станции:', siteId);
    
    try {
      const site = await sitesAPI.get(siteId);
      store.dispatch({ type: 'SELECT_SITE', payload: site });

      store.dispatch({ type: 'SET_SITE_TIMESERIES_LOADING', payload: true });
      try {
        const tsData = await sitesAPI.getTimeSeries(siteId);
        store.dispatch({ type: 'SET_SITE_TIMESERIES', payload: tsData });
      
        // Автовыбор первого параметра
        const params = (tsData.columns || []).filter(c => c !== 'date' && c !== 'sample_id');
        if (params.length > 0) {
          store.dispatch({ type: 'SET_SELECTED_SITE_PARAMETER', payload: params[0] });
        }
      } catch (e) {
        console.error('❌ Ошибка загрузки временного ряда:', e);
        store.dispatch({ type: 'SET_SITE_TIMESERIES', payload: { columns: [], rows: [] } });
      } finally {
        store.dispatch({ type: 'SET_SITE_TIMESERIES_LOADING', payload: false });
      }

      map.getView().animate({ center: evt.coordinate, duration: 300 });
      return;
    } catch (error) {
      console.error('Ошибка запроса станции:', error);
    }
  }
  
  // 3. Если точка не найдена, пытаемся найти маршрут
  const traverseId = await getFeatureIdFromLayer('traverses', evt, viewResolution);
  if (traverseId && isValidUUID(traverseId)) {
    console.log('🛤️ Запрос данных маршрута:', traverseId);
    
    try {
      const [traverseFull, traverseInfo] = await Promise.all([
        traversesAPI.getFull(traverseId),
        traversesAPI.getInfo(traverseId)
      ]);

      const mergedTraverse: TraverseInfo = {
        id: traverseFull.id,
        num: traverseFull.num,
        date_start: traverseFull.date_start,
        date_end: traverseFull.date_end,
        description: traverseFull.description,
        expedition: traverseFull.expedition,
        vessel: traverseFull.vessel,
        length: traverseInfo.length,
        points: traverseInfo.points,
        samples: traverseInfo.samples
      };

      const enrichedTraverse = await enrichTraverse(mergedTraverse);
      store.dispatch({ type: 'SELECT_TRAVERSE', payload: enrichedTraverse });
            
      map.getView().animate({ center: evt.coordinate, duration: 300 });
      return;
    } catch (error) {
      console.error('Ошибка запроса маршрута:', error);
    }
  }

  // 4. Ничего не найдено
  store.dispatch({ type: 'CLEAR_SELECTION' });
}

async function getFeatureIdFromLayer(
  layerId: string, 
  evt: any, 
  viewResolution: number
): Promise<string | null> {
  const layer = getLayerById(layerId);
  console.log('layerId = ', layerId);
  if (!layer || !getEffectiveVisibility(layerId)) return null;
  console.log('visibility passed');

  const source = layer.getSource();
  if (!source) return null;
  console.log('source passed');

  // Если это векторный слой (WFS)
  if ('getFeaturesAtCoordinate' in source || 'getClosestFeatureToCoordinate' in source) {
    let clickedFeature: any = null;
    
    console.log('is vector layer');
    
    map.forEachFeatureAtPixel(evt.pixel, (feature, layerObj) => {
      if (layerObj === layer) {
        clickedFeature = feature;
        console.log('found (1)');
        return true; // остановить перебор
      }
      console.log('not found');
      return undefined;
    });

    if (clickedFeature) {
      // Пытаемся получить ID из разных возможных полей
      const props = clickedFeature.getProperties();
      const featureId = props.id || props.ID || props.fid || clickedFeature.getId();
      
      console.log('featureId = ', featureId);
      
      if (featureId && isValidUUID(String(featureId))) {
        return String(featureId);
      }
    }
    
    return null;
  }

  // Если это растровый слой (WMS)
  if ('getFeatureInfoUrl' in source) {
    try {
      const url = (source as any).getFeatureInfoUrl(
        evt.coordinate, 
        viewResolution, 
        'EPSG:3857', 
        { INFO_FORMAT: 'application/json' }
      );

      if (!url) return null;

      const response = await fetch(url);
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const feature = data.features[0];
        let featureId = feature.id;
        
        if (!isValidUUID(featureId) && feature.properties?.id) {
          featureId = feature.properties.id;
        }
        
        if (isValidUUID(featureId)) {
          return featureId;
        }
      }
    } catch (error) {
      console.error(`Ошибка запроса GetFeatureInfo для слоя ${layerId}:`, error);
    }
  }

  return null;
}

// ============================================================================
// Получение координат точки (широта, долгота)
// ============================================================================

export function getFeatureCoordinates(
  layerId: string, 
  evt: any
): { lat: number; lon: number } | null {
  const layer = getLayerById(layerId);
  if (!layer || !layer.getVisible()) return null;

  const source = layer.getSource();
  if (!source) return null;

  // Работает только для векторных слоёв
  if (!('getFeaturesAtCoordinate' in source || 'getClosestFeatureToCoordinate' in source)) {
    return null;
  }

  let clickedFeature: any = null;
  
  map.forEachFeatureAtPixel(evt.pixel, (feature, layerObj) => {
    if (layerObj === layer) {
      clickedFeature = feature;
      return true;
    }
    return undefined;
  });

  if (!clickedFeature) return null;

  const geom = clickedFeature.getGeometry();
  if (!geom) return null;

  const geomType = geom.getType();
  
  // Проверяем, является ли геометрия точкой
  if (geomType === 'Point') {
    const coords = geom.getCoordinates();
    // Конвертируем из EPSG:3857 в WGS84 (EPSG:4326)
    const [lon, lat] = toLonLat(coords);
    return { lat, lon };
  }

  return null;
}

// ============================================================================
// Утилиты
// ============================================================================

export function updateMapSize(): void {
  if (map) map.updateSize();
}

export function getMap(): OLMap {
  return map;
}